import {
  WSAlert,
  WSChip,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSInfiniteScroll,
  WSList,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayerStatus } from "@wingspanhq/wingspan-api-sdk";
import { Route, Switch, useHistory } from "react-router-dom";
import { queriesPayerRows } from "@query";
import { useAddClientModal } from "../components/AddClientModal/useAddClientModal";
import { TablePayers } from "../components/TablePayers/TablePayers";

import { useCallback } from "react";
import { Tabs } from "../../../components/Tabs";
import { KEYBOARD_SHORTCUT_SEARCH } from "../../../constants/keyboardShortcuts";
import { useUserId } from "../../../query/hooks/helpers";
import { useQueryRequirementsList } from "../../../query/requirements/queries/useQueryRequirementsList";
import { useQueryActivities } from "../../../query/users/queries";
import pluralize from "pluralize";
import { useOpenModalBySearchParam } from "../../../utils/useOpenModalBySearchParam";
import { useRedirectToByKeyPress } from "../../../utils/useRedirectToByKeyPress";
import { useCustomization } from "../../customization";
import { selectorExpiredRequirements } from "../../Requirements/selectors/selectorExpiredRequirements";
import { selectorExpiringRequirements } from "../../Requirements/selectors/selectorExpiringRequirements";
import { selectorIsAllRequirementsComplete } from "../../Requirements/selectors/selectorIsAllRequirementsComplete";
import { PAYERS_PATH, PAYERS_REQUIREMENTS_PATH } from "../paths";
import { usePayerRequirements } from "../../Requirements/modules/custom/payer/usePayerRequirements";
import { RouteClientsRequirements } from "./RouteClientsRequirements";
import styles from "./styles.module.scss";

export const RouteClients: React.FC = () => {
  const { terminology } = useCustomization();
  const history = useHistory();
  const userId = useUserId();
  const addClientModal = useAddClientModal();
  useOpenModalBySearchParam("add_client", addClientModal);

  // v3 clients list — active clients off the denormalized search rows
  // (searchPayerRows), the twin of the v1 search-service /payer-row. Server-side
  // status filter + cursor pagination (infinite scroll below). Archived clients
  // (status Deactivated) have their own route; free-text search is a separate route.
  const queryPayers = queriesPayerRows.searchPayerRows.usePaginatedQuery({
    // filter: { status: { eq: PayerStatus.ACTIVATED } }
  });
  const payers = queryPayers.flatData ?? [];
  const totalCount =
    queryPayers.data?.pages?.[0]?.pagination?.totalSize ?? payers.length;

  const queryActivity = useQueryActivities(userId);
  const queryRequirements = useQueryRequirementsList({
    filter: {
      payeeId: userId,
      payerPayeeEngagementIds: {
        "!=": "null"
      }
    },
    sort: {
      updatedAt: "desc"
    }
  });

  const onAddClient = useCallback(async () => {
    // v3 create returns a payer id in a different namespace than the still-v1
    // Clients detail route, so we no longer navigate to the detail page after
    // adding. The new client surfaces in the list once the v3 mutation
    // invalidates the v1 payer caches (the backend indexes it into payer-row).
    await addClientModal.open();
  }, [addClientModal]);

  const activity = queryActivity.data;

  useRedirectToByKeyPress(KEYBOARD_SHORTCUT_SEARCH, `${PAYERS_PATH}/search`);

  const requirements = usePayerRequirements(queryRequirements.data || []);

  const isRequirementsComplete =
    selectorIsAllRequirementsComplete(requirements);

  const expiringRequirements = selectorExpiringRequirements(
    queryRequirements.data || []
  );

  const expiredRequirements = selectorExpiredRequirements(
    queryRequirements.data || []
  );

  return (
    <WSPage>
      <WSPageToolbar
        title={pluralize.plural(terminology().getPaidClient)}
        actions={[
          {
            label: "Search",
            icon: "search",
            buttonKind: "Tertiary",
            hideButtonOn: false,
            buttonOrder: 0,
            onClick: () => history.push(`${PAYERS_PATH}/search`)
          },
          {
            label: "Add client",
            icon: "plus",
            buttonKind: "Primary",
            onClick: onAddClient
          },
          activity?.flows?.personalPayLinkSetup?.complete
            ? {
                icon: "link",
                label: "View custom pay link",
                onClick: () => {
                  history.push("/member/personal-pay-link/dashboard");
                }
              }
            : {
                icon: "link",
                label: "Get a custom pay link",
                helpText: "Click to learn more",
                onClick: () => {
                  history.push("/member/personal-pay-link/overview");
                }
              },
          {
            icon: "archive",
            label: "View archived clients",
            onClick: () => {
              history.push(`${PAYERS_PATH}/archived`);
            }
          }
        ]}
      />
      {(expiringRequirements.length > 0 || expiredRequirements.length > 0) && (
        <WSList mb="L">
          {expiredRequirements.length > 0 && (
            <WSAlert
              theme="warning"
              icon="alert"
              title="Requirements incomplete"
              action={{
                text: "View requirements",
                onClick: () => {
                  history.push(PAYERS_REQUIREMENTS_PATH);
                }
              }}
            >
              You must complete all eligibility requirements for an engagement
              before you can receive payments for work under that engagement.
            </WSAlert>
          )}

          {expiringRequirements.length > 0 && (
            <WSAlert
              theme="neutral"
              icon="calendar"
              title="Requirements expiring soon"
              action={{
                text: "View requirements",
                onClick: () => {
                  history.push(PAYERS_REQUIREMENTS_PATH);
                }
              }}
            >
              You have {expiringRequirements.length} requirements expiring soon.
            </WSAlert>
          )}
        </WSList>
      )}

      {requirements.length ? (
        <Tabs
          mb="L"
          tabs={[
            {
              label: "Clients",
              path: PAYERS_PATH
            },
            {
              label: "Requirements",
              path: PAYERS_REQUIREMENTS_PATH,
              pill: {
                icon: true,
                theme: isRequirementsComplete ? "success" : "warning"
              }
            }
          ]}
        />
      ) : null}
      <Switch>
        <Route
          path={PAYERS_PATH}
          exact
          render={() => (
            <>
              <WSFlexBox.CenterY mb="L" className={styles.statsContainer}>
                <WSElement className={styles.resultsStats}>
                  <WSText.ParagraphXs color="gray500">
                    Showing:
                  </WSText.ParagraphXs>
                  <WSChip
                    pillText={`${totalCount} item${
                      totalCount === 1 ? "" : "s"
                    }`}
                    theme="neutral"
                    pillType="noBackground"
                  />
                </WSElement>
              </WSFlexBox.CenterY>
              {!queryPayers.isLoading && payers.length === 0 ? (
                <WSEmptyState
                  hasBorder
                  bgColor="gray"
                  type="people"
                  title="No clients yet"
                  description="Once a client is added, they will appear here."
                  actions={{
                    buttons: [
                      {
                        label: "Add client",
                        kind: "Secondary",
                        onClick: onAddClient
                      }
                    ]
                  }}
                />
              ) : (
                <WSInfiniteScroll
                  onLoad={() => queryPayers.fetchNextPage()}
                  loadMore={payers.length > 0}
                  endOfList={!queryPayers.hasNextPage}
                  loading={!!queryPayers.isFetchingNextPage}
                >
                  <TablePayers
                    payers={payers}
                    loading={queryPayers.isLoading}
                    onRowClick={payer =>
                      history.push(`${PAYERS_PATH}/${payer.id}`)
                    }
                  />
                </WSInfiniteScroll>
              )}
            </>
          )}
        />
        <Route
          render={() => (
            <RouteClientsRequirements
              isLoading={queryRequirements.isFetching}
              requirements={requirements}
            />
          )}
          path={PAYERS_REQUIREMENTS_PATH}
          exact
        />
      </Switch>
    </WSPage>
  );
};
